import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, PlusCircle, Search, Filter, ArrowRight, ListFilter, WholeWord } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';

const ExperienceLibraryPage = ({ session, profile }: { session: any; profile: any }) => {
  const navigate = useNavigate();

  const actionCards = [
    {
      title: 'Create New Experience',
      description: 'Design and add a new experience to your library',
      icon: <PlusCircle className="h-8 w-8" />,
      color: 'bg-blue-500',
      path: '/dashboard/catalog/create'
    },
    {
      title: 'Browse Experiences',
      description: 'Explore your complete catalog of experiences',
      icon: <BookOpen className="h-8 w-8" />,
      color: 'bg-purple-500',
      path: '/dashboard/catalog'
    },
    {
      title: 'UN Campaigns',
      description: 'Manage and track UN campaigns',
      icon: <WholeWord className="h-8 w-8" />,
      color: 'bg-green-500',
      path: '/dashboard/campaigns'
    }
  ];

  // Sample experience categories
  const categories = [
    { name: 'Team Building', count: 0 },
    { name: 'Environmental', count: 0 },
    { name: 'Education', count: 0 },
    { name: 'Community Service', count: 0 },
    { name: 'Health & Wellness', count: 0 },
    { name: 'Skills-based', count: 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="mb-8"
      >
        <h1 className="text-3xl font-bold mb-2">Experience Library</h1>
        <p className="text-muted-foreground">
          Manage and showcase your organization's volunteer experiences
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-grow">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input 
              placeholder="Search experiences..." 
              className="pl-10 w-full"
            />
          </div>
        </div>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" /> Filters
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {actionCards.map((card, index) => (
          <Card key={index} className="overflow-hidden transition-all hover:shadow-lg">
            <div className={`${card.color} p-4 flex justify-center items-center text-white`}>
              {card.icon}
            </div>
            <CardHeader>
              <CardTitle>{card.title}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full" 
                onClick={() => navigate(card.path)}
              >
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Experience Categories</CardTitle>
            <CardDescription>Browse experiences by category</CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="flex items-center gap-2">
            <ListFilter className="h-4 w-4" /> View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {categories.map((category, index) => (
              <Button 
                key={index} 
                variant="outline" 
                className="h-auto py-6 justify-between hover:bg-slate-50"
                onClick={() => navigate(`/dashboard/catalog?category=${category.name}`)}
              >
                <span className="text-lg font-medium">{category.name}</span>
                <span className="bg-slate-100 px-2 py-1 rounded-full text-sm">
                  {category.count}
                </span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExperienceLibraryPage;
